import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  ReferenceField,
  FunctionField,
  useRecordContext,
  useTranslate,
  TopToolbar,
  CreateButton,
  useRedirect,
  ExportButton,
} from 'react-admin';
import { Chip, Button, Box, Typography, Avatar, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { CustomEmptyState } from '../../components/CustomEmptyState';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PublicIcon from '@mui/icons-material/Public';
import HomeIcon from '@mui/icons-material/Home';
import { PageTitle } from '../../components/PageTitle';

const ExperienceRadiusField = () => {
  const record = useRecordContext();
  if (!record?.experienceRadius) {
    return (
      <Typography variant="body2" color="text.secondary">
        -
      </Typography>
    );
  }

  const getConfig = (radius: string) => {
    switch (radius) {
      case 'LOCAL':
        return {
          color: 'success' as const,
          icon: <HomeIcon />,
          label: 'Local Delivery',
        };
      case 'REGIONAL':
        return {
          color: 'warning' as const,
          icon: <LocalShippingIcon />,
          label: 'Regional',
        };
      case 'LONG_HAUL':
        return {
          color: 'primary' as const,
          icon: <PublicIcon />,
          label: 'Long Haul',
        };
      default:
        return {
          color: 'default' as const,
          icon: null,
          label: radius,
        };
    }
  };

  const config = getConfig(record.experienceRadius);

  return (
    <Chip
      label={config.label}
      color={config.color}
      size="small"
      icon={config.icon}
      title="Experience Radius"
      sx={{ fontWeight: 500 }}
    />
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ProfileStateField = () => {
  const record = useRecordContext();
  if (!record?.profileState) {
    return (
      <Typography variant="body2" color="text.secondary">
        -
      </Typography>
    );
  }

  const getConfig = (state: string) => {
    switch (state) {
      case 'COMPLETE':
        return {
          color: 'success' as const,
          variant: 'filled' as const,
          label: 'Complete',
        };
      case 'INCOMPLETE':
        return {
          color: 'warning' as const,
          variant: 'outlined' as const,
          label: 'Incomplete',
        };
      case 'PENDING':
        return {
          color: 'info' as const,
          variant: 'outlined' as const,
          label: 'Pending',
        };
      default:
        return {
          color: 'default' as const,
          variant: 'outlined' as const,
          label: state,
        };
    }
  };

  const config = getConfig(record.profileState);

  return (
    <Chip
      label={config.label}
      color={config.color}
      size="small"
      variant={config.variant}
      title="Profile State"
      sx={{ fontWeight: 500 }}
    />
  );
};

const DriverListActions = () => {
  const navigate = useNavigate();

  return (
    <TopToolbar>
      <CreateButton />
      <Button
        variant="outlined"
        onClick={() => navigate('/drivers/profile-builder')}
        sx={{ ml: 1 }}
      >
        Profile Setup
      </Button>
    </TopToolbar>
  );
};

const DriverEmptyState = () => {
  const navigate = useNavigate();
  const translate = useTranslate();

  return (
    <CustomEmptyState
      resource="drivers"
      title={translate('wizard.empty_state.title', { defaultValue: 'No Drivers Yet' })}
      subtitle={translate('wizard.empty_state.subtitle', {
        defaultValue:
          'Start building your driver database by creating comprehensive driver profiles. Track licenses, certifications, employment history, and more.',
      })}
      actionText={translate('wizard.empty_state.action', { defaultValue: 'Create Driver Profile' })}
      onAction={() => navigate('/drivers/wizard')}
    />
  );
};

const DriverNameField = () => {
  const record = useRecordContext();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const redirect = useRedirect();

  if (!record) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Avatar sx={{ bgcolor: '#1976d2' }}>
        <PersonIcon />
      </Avatar>
      <Box>
        <Typography variant="body1" fontWeight="medium">
          <ReferenceField source="userId" reference="users" link={false}>
            <TextField source="name" />
          </ReferenceField>
        </Typography>
      </Box>
    </Box>
  );
};

const CitizenshipStatusField = () => {
  const record = useRecordContext();
  if (!record) {
    return null;
  }

  return (
    <Stack direction="row" spacing={1}>
      {record.canadianCitizen && (
        <Chip
          icon={<CheckCircleIcon />}
          label="Canadian"
          size="small"
          color="success"
          variant="outlined"
        />
      )}
      {record.hasValidPassport && (
        <Chip
          icon={<CheckCircleIcon />}
          label="Valid Passport"
          size="small"
          color="info"
          variant="outlined"
        />
      )}
      {record.canEnterUsa && (
        <Chip
          icon={<PublicIcon />}
          label="USA Entry"
          size="small"
          color="primary"
          variant="outlined"
        />
      )}
      {!record.canadianCitizen && !record.hasValidPassport && !record.canEnterUsa && (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      )}
    </Stack>
  );
};

const CreatedDateField = () => {
  const record = useRecordContext();
  if (!record?.createdAt) {
    return null;
  }

  const date = new Date(record.createdAt);
  const formattedDate = date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  return (
    <Typography variant="body2" color="text.secondary">
      {formattedDate}
    </Typography>
  );
};

export const DriverList = () => {
  const redirect = useRedirect();

  return (
    <Box>
      <PageTitle
        title="Drivers"
        titleKey="pages.drivers"
        subtitle="Manage your driver roster"
        subtitleKey="pages.drivers.subtitle"
        icon={<PersonIcon />}
      />
      <List
        perPage={25}
        sort={{ field: 'id', order: 'DESC' }}
        filters={[]}
        actions={<DriverListActions />}
        empty={<DriverEmptyState />}
        sx={{ '& .RaList-main': { mt: 0 } }}
      >
      <Datagrid
        bulkActionButtons={false}
        rowClick={id => redirect('show', 'drivers', id)}
        sx={{
          '& .RaDatagrid-headerCell': {
            backgroundColor: '#f5f5f5',
            fontWeight: 600,
          },
          '& .RaDatagrid-row': {
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
            },
          },
          '& .RaDatagrid-cell': {
            padding: '16px',
          },
        }}
      >
        <FunctionField label="Driver" render={() => <DriverNameField />} sx={{ minWidth: 250 }} />
        <ExperienceRadiusField label="Experience" />
        <FunctionField
          label="Documentation"
          render={() => <CitizenshipStatusField />}
          sx={{ minWidth: 300 }}
        />
        <FunctionField label="Added" render={() => <CreatedDateField />} />
      </Datagrid>
    </List>
    </Box>
  );
};
